import axios from 'axios';
import { Device, DeviceResponse, ApiResponse, Task, TaskResponse, TaskCreateRequest, TaskUpdateRequest, Firmware, FirmwareResponse, FileStore, StoredFileContent, TemplateInfo, LayoutInfo, DashboardWidget, WidgetCatalogItem, DashboardTopicValue, FileTreeResponse, ConfigLibraryTreeResponse, FileSyncPreview } from './types';
import { ConfigSchema } from './schema/schema';
import { LayoutDefinition } from './layout/types';
import { SignalsDefinition } from './signals/types';
import { PlacedSignal } from './shopfloor/types';
import { AlertChannelsDraft, AlertChannelsPublic, AlertEvent, emptyChannelsPublic, TemperatureAlertRule } from './alerting/types';

const API_BASE = '/api';

export const deviceApi = {
  // Get all devices
  getDevices: async (): Promise<Device[]> => {
    const response = await axios.get<DeviceResponse>(`${API_BASE}/devices`);
    return response.data.devices || [];
  },

  // Get single device
  getDevice: async (id: string): Promise<Device> => {
    const response = await axios.get<Device>(`${API_BASE}/devices/${id}`);
    return response.data;
  },

  // Register/update device (for testing)
  registerDevice: async (device: Partial<Device>): Promise<ApiResponse> => {
    const response = await axios.post<ApiResponse>(`${API_BASE}/devices/register`, device);
    return response.data;
  },

  updateDevice: async (id: string, patch: { name?: string }): Promise<Device> => {
    const response = await axios.patch<{ ok: boolean; device: Device }>(`${API_BASE}/devices/${id}`, patch);
    return response.data.device;
  },

  getStoredFile: async (id: string, path: string): Promise<{ path: string; content: string; size: number; updatedAt: number } | null> => {
    try {
      const response = await axios.get(`${API_BASE}/devices/${id}/stored-files`, { params: { path } });
      return response.data;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },

  saveStoredFile: async (id: string, path: string, content: string): Promise<void> => {
    await axios.put(`${API_BASE}/devices/${id}/stored-files`, { path, content });
  },

  renameStoredFile: async (id: string, from: string, to: string): Promise<StoredFileContent> => {
    const response = await axios.post<{ ok: boolean; file: StoredFileContent }>(`${API_BASE}/devices/${id}/stored-files/rename`, { from, to });
    return response.data.file;
  },

  deleteStoredFile: async (id: string, path: string): Promise<void> => {
    await axios.delete(`${API_BASE}/devices/${id}/stored-files`, { params: { path } });
  },

  pullStoredFile: async (id: string, path: string): Promise<Task> => {
    const response = await axios.post<{ ok: boolean; task: Task }>(`${API_BASE}/devices/${id}/stored-files/pull`, { path });
    return response.data.task;
  },

  pushStoredFile: async (id: string, path: string): Promise<Task> => {
    const response = await axios.post<{ ok: boolean; task: Task }>(`${API_BASE}/devices/${id}/stored-files/push`, { path });
    return response.data.task;
  },

  getFileSync: async (id: string): Promise<FileSyncPreview> => {
    const response = await axios.get<{ ok: boolean } & FileSyncPreview>(`${API_BASE}/devices/${id}/file-sync`);
    const { ok, ...preview } = response.data;
    return preview;
  },

  applyFileSync: async (
    id: string,
    body?: { prefer?: 'server' | 'device'; actions?: Array<{ path: string; action: 'push' | 'pull' }> },
  ): Promise<{ preview: FileSyncPreview; tasks: Task[]; skipped: Array<{ path?: string; error?: string }> }> => {
    const response = await axios.post<{ ok: boolean; preview: FileSyncPreview; tasks: Task[]; skipped: Array<{ path?: string; error?: string }> }>(
      `${API_BASE}/devices/${id}/file-sync`,
      body || {},
    );
    return { preview: response.data.preview, tasks: response.data.tasks || [], skipped: response.data.skipped || [] };
  },

  copyFromTemplate: async (id: string, templateId: string, path?: string): Promise<StoredFileContent> => {
    const response = await axios.post<{ ok: boolean; copied: string[]; file: StoredFileContent }>(
      `${API_BASE}/devices/${id}/stored-files/from-template`,
      { templateId, path }
    );
    return response.data.file;
  },

  getStoredFileTree: async (id: string): Promise<FileTreeResponse> => {
    const response = await axios.get<FileTreeResponse>(`${API_BASE}/devices/${id}/stored-files/tree`);
    return response.data;
  },

  // Health check
  healthCheck: async (): Promise<boolean> => {
    try {
      const response = await axios.get(`${API_BASE}/health`);
      return response.data.ok === true;
    } catch {
      return false;
    }
  }
};

export const fileStoreApi = {
  getStore: async (): Promise<FileStore> => {
    const response = await axios.get<FileStore>(`${API_BASE}/file-store`);
    return response.data;
  },

  getSchema: async (): Promise<ConfigSchema> => {
    const response = await axios.get<ConfigSchema>(`${API_BASE}/config-schema`);
    return response.data;
  },

  getFileSchema: async (id: string): Promise<ConfigSchema> => {
    const response = await axios.get<ConfigSchema>(`${API_BASE}/schemas/${encodeURIComponent(id)}`);
    return response.data;
  },

  getRevisions: async (kind: 'device' | 'template', id: string, path: string): Promise<{ id: string; size: number; updatedAt: number }[]> => {
    const url = kind === 'template'
      ? `${API_BASE}/templates/${id}/files/revisions`
      : `${API_BASE}/devices/${id}/stored-files/revisions`;
    const response = await axios.get<{ revisions: { id: string; size: number; updatedAt: number }[] }>(url, { params: { path } });
    return response.data.revisions || [];
  },

  getRevision: async (kind: 'device' | 'template', id: string, path: string, revId: string): Promise<string> => {
    const url = kind === 'template'
      ? `${API_BASE}/templates/${id}/files/revisions/${revId}`
      : `${API_BASE}/devices/${id}/stored-files/revisions/${revId}`;
    const response = await axios.get<{ content: string }>(url, { params: { path } });
    return response.data.content;
  },

  getTemplateFile: async (id: string, path: string): Promise<StoredFileContent | null> => {
    try {
      const response = await axios.get<{ file: StoredFileContent }>(`${API_BASE}/templates/${id}`, { params: { path } });
      return response.data.file;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },

  saveTemplateFile: async (id: string, path: string, content: string): Promise<void> => {
    await axios.put(`${API_BASE}/templates/${id}/files`, { path, content });
  },

  renameTemplateFile: async (id: string, from: string, to: string): Promise<StoredFileContent> => {
    const response = await axios.post<{ ok: boolean; file: StoredFileContent }>(`${API_BASE}/templates/${id}/files/rename`, { from, to });
    return response.data.file;
  },

  deleteTemplateFile: async (id: string, path: string): Promise<void> => {
    await axios.delete(`${API_BASE}/templates/${id}/files`, { params: { path } });
  },

  createTemplate: async (body: {
    name: string;
    model?: string;
    fromDeviceId?: string;
    path?: string;
    content?: string;
  }): Promise<TemplateInfo> => {
    const response = await axios.post<{ ok: boolean; template: TemplateInfo }>(`${API_BASE}/templates`, body);
    return response.data.template;
  },

  updateTemplate: async (id: string, patch: { name?: string; model?: string }): Promise<TemplateInfo> => {
    const response = await axios.patch<{ ok: boolean; template: TemplateInfo }>(`${API_BASE}/templates/${id}`, patch);
    return response.data.template;
  },

  deleteTemplate: async (id: string): Promise<TemplateInfo[]> => {
    const response = await axios.delete<{ ok: boolean; templates: TemplateInfo[] }>(`${API_BASE}/templates/${id}`);
    return response.data.templates;
  },

  getTemplateFileTree: async (id: string): Promise<FileTreeResponse> => {
    const response = await axios.get<FileTreeResponse>(`${API_BASE}/templates/${id}/stored-files/tree`);
    return response.data;
  },

  getConfigLibraryTree: async (): Promise<ConfigLibraryTreeResponse> => {
    const response = await axios.get<ConfigLibraryTreeResponse>(`${API_BASE}/config-library/tree`);
    return response.data;
  },
};

export const signalsApi = {
  list: async (): Promise<LayoutInfo[]> => {
    const response = await axios.get<{ signals: LayoutInfo[] }>(`${API_BASE}/signals`);
    return response.data.signals || [];
  },

  get: async (name: string): Promise<SignalsDefinition> => {
    const response = await axios.get<{ signal: SignalsDefinition }>(`${API_BASE}/signals/${encodeURIComponent(name)}`);
    return response.data.signal;
  },

  save: async (name: string, signal: SignalsDefinition | Record<string, unknown>): Promise<LayoutInfo> => {
    const response = await axios.put<{ ok: boolean; signal: LayoutInfo }>(`${API_BASE}/signals/${encodeURIComponent(name)}`, { signal });
    return response.data.signal;
  },

  delete: async (name: string): Promise<LayoutInfo[]> => {
    const response = await axios.delete<{ ok: boolean; signals: LayoutInfo[] }>(`${API_BASE}/signals/${encodeURIComponent(name)}`);
    return response.data.signals || [];
  },
};

export const layoutApi = {
  list: async (): Promise<LayoutInfo[]> => {
    const response = await axios.get<{ layouts: LayoutInfo[] }>(`${API_BASE}/layouts`);
    return response.data.layouts || [];
  },

  get: async (name: string): Promise<LayoutDefinition> => {
    const response = await axios.get<{ name: string; path: string; layout: LayoutDefinition }>(`${API_BASE}/layouts/${encodeURIComponent(name)}`);
    return response.data.layout;
  },

  save: async (name: string, layout: LayoutDefinition): Promise<LayoutInfo> => {
    const response = await axios.put<{ ok: boolean; layout: LayoutInfo }>(`${API_BASE}/layouts/${encodeURIComponent(name)}`, { layout });
    return response.data.layout;
  },

  remove: async (name: string): Promise<void> => {
    await axios.delete(`${API_BASE}/layouts/${encodeURIComponent(name)}`);
  },
};

export type FlowDeployFileAction = 'create' | 'update' | 'delete';

export type FlowDeployFile = {
  path: string;
  action: FlowDeployFileAction;
};

export type FlowDeployController = {
  deviceId: string;
  hostname: string;
  files: FlowDeployFile[];
};

export type FlowDeployPreview = {
  reason?: 'bundled' | 'disabled' | 'noRoutes' | 'noControllers' | 'noChanges' | null;
  controllers: FlowDeployController[];
  widgets?: { id: string; type: string; title?: string }[];
};

export type FlowDeployJob = {
  deviceId: string;
  hostname: string;
  path: string;
  action: FlowDeployFileAction;
  taskId: string;
};

export type FlowDeployResult = FlowDeployPreview & {
  ok: boolean;
  flow?: LayoutInfo;
  jobs: FlowDeployJob[];
};

export const flowApi = {
  list: async (): Promise<LayoutInfo[]> => {
    const response = await axios.get<{ flows: LayoutInfo[] }>(`${API_BASE}/flows`);
    return response.data.flows || [];
  },

  get: async (name: string): Promise<Record<string, unknown>> => {
    const response = await axios.get<{ name: string; path: string; flow: Record<string, unknown> }>(`${API_BASE}/flows/${encodeURIComponent(name)}`);
    return response.data.flow;
  },

  save: async (name: string, flow: Record<string, unknown>): Promise<{
    flow: LayoutInfo;
  }> => {
    const response = await axios.put<{
      ok: boolean;
      flow: LayoutInfo;
    }>(`${API_BASE}/flows/${encodeURIComponent(name)}`, { flow });
    return response.data;
  },

  previewDeploy: async (name: string, flow: Record<string, unknown>): Promise<FlowDeployPreview> => {
    const response = await axios.post<FlowDeployPreview>(
      `${API_BASE}/flows/${encodeURIComponent(name)}/deploy-preview`,
      { flow },
    );
    return response.data;
  },

  deploy: async (name: string, flow: Record<string, unknown>): Promise<FlowDeployResult> => {
    const response = await axios.post<FlowDeployResult>(
      `${API_BASE}/flows/${encodeURIComponent(name)}/deploy`,
      { flow },
    );
    return response.data;
  },

  remove: async (name: string): Promise<void> => {
    await axios.delete(`${API_BASE}/flows/${encodeURIComponent(name)}`);
  },
};

export const taskApi = {
  // Get all tasks for a device
  getTasks: async (deviceId: string): Promise<Task[]> => {
    const response = await axios.get<TaskResponse>(`${API_BASE}/devices/${deviceId}/tasks`);
    return response.data.tasks || [];
  },

  // Create a new task
  createTask: async (deviceId: string, taskData: TaskCreateRequest): Promise<Task> => {
    const response = await axios.post<{ ok: boolean; task: Task }>(`${API_BASE}/devices/${deviceId}/tasks`, taskData);
    return response.data.task;
  },

  // Update task state
  updateTask: async (deviceId: string, taskId: string, updateData: TaskUpdateRequest): Promise<Task> => {
    const response = await axios.put<{ ok: boolean; task: Task }>(`${API_BASE}/devices/${deviceId}/tasks/${taskId}`, updateData);
    return response.data.task;
  },

  // Cancel a task
  cancelTask: async (deviceId: string, taskId: string): Promise<Task> => {
    const response = await axios.put<{ ok: boolean; task: Task }>(`${API_BASE}/devices/${deviceId}/tasks/${taskId}/cancel`);
    return response.data.task;
  },

  queueOta: async (deviceId: string, filename?: string): Promise<Task> => {
    const response = await axios.post<{ ok: boolean; task: Task }>(
      `${API_BASE}/devices/${deviceId}/ota`,
      filename ? { filename } : {},
    );
    return response.data.task;
  },

  waitForTask: async (deviceId: string, taskId: string, timeoutMs = 90000): Promise<Task> => {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      const tasks = await taskApi.getTasks(deviceId);
      const task = tasks.find((t) => t.id === taskId);
      if (task && ['completed', 'failed', 'cancelled'].includes(task.state)) {
        return task;
      }
      await new Promise((r) => setTimeout(r, 2000));
    }
    throw new Error('Timed out waiting for the device to pick up the job');
  }
};

export const firmwareApi = {
  getFirmware: async (environment?: string, model?: string): Promise<Firmware[]> => {
    const params: Record<string, string> = {};
    if (environment) {
      params.environment = environment;
    }
    if (model) {
      params.model = model;
    }
    const response = await axios.get<FirmwareResponse>(`${API_BASE}/firmware`, { params });
    return response.data.firmware || [];
  },

  getLatestFirmware: async (environment?: string): Promise<Firmware | null> => {
    try {
      const response = await axios.get<{ firmware: Firmware }>(`${API_BASE}/firmware/latest`, {
        params: environment ? { environment } : {},
      });
      return response.data.firmware || null;
    } catch (err: any) {
      if (err?.response?.status === 404) {
        return null;
      }
      throw err;
    }
  },

  downloadFirmware: async (filename: string): Promise<Blob> => {
    const response = await axios.get(`${API_BASE}/firmware/${filename}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  downloadFirmwareBytes: async (filename: string): Promise<Uint8Array> => {
    const blob = await firmwareApi.downloadFirmware(filename);
    return new Uint8Array(await blob.arrayBuffer());
  },

  getStoredSettings: async (deviceId: string): Promise<string> => {
    const stored = await deviceApi.getStoredFile(deviceId, '/settings.json');
    return stored?.content || '';
  },

  provision: async (body: {
    mac: string;
    model?: string;
    hostname?: string;
    name?: string;
    settings?: string;
  }): Promise<{ deviceId: string }> => {
    const response = await axios.post<{ ok: boolean; deviceId: string }>(`${API_BASE}/provision`, body);
    return { deviceId: response.data.deviceId };
  },
};

export const dashboardApi = {
  getCatalog: async (): Promise<WidgetCatalogItem[]> => {
    const response = await axios.get<{ widgets: WidgetCatalogItem[] }>(`${API_BASE}/widgets/catalog`);
    return response.data.widgets || [];
  },

  getDashboard: async (): Promise<{ widgets: DashboardWidget[] }> => {
    const response = await axios.get<{ widgets: DashboardWidget[] }>(`${API_BASE}/dashboard`);
    return { widgets: response.data.widgets || [] };
  },

  saveDashboard: async (body: { widgets: DashboardWidget[] }): Promise<{ widgets: DashboardWidget[] }> => {
    const response = await axios.put<{ ok: boolean; widgets: DashboardWidget[] }>(`${API_BASE}/dashboard`, body);
    return { widgets: response.data.widgets || [] };
  },

  getValues: async (topics?: string[]): Promise<{ values: Record<string, DashboardTopicValue | null>; mqttConfigured: boolean }> => {
    const params = topics && topics.length
      ? { topics: topics.join(',') }
      : undefined;
    const response = await axios.get<{
      values: Record<string, DashboardTopicValue | null>;
      mqttConfigured: boolean;
    }>(`${API_BASE}/dashboard/values`, { params });
    return {
      values: response.data.values || {},
      mqttConfigured: response.data.mqttConfigured !== false,
    };
  },

  publishMqtt: async (topic: string, payload: string, opts?: { retain?: boolean }): Promise<void> => {
    await axios.post(`${API_BASE}/mqtt/publish`, {
      topic,
      payload,
      retain: opts?.retain,
    });
  },

  commandRelay: async (body: {
    hostname?: string;
    of: string;
    key: string;
    on: boolean;
  }): Promise<{ ok: boolean; transport?: string; topic?: string }> => {
    const response = await axios.post<{ ok: boolean; transport?: string; topic?: string }>(
      `${API_BASE}/relay/command`,
      body,
    );
    return response.data;
  },

  /** Flow-first stacklight command (MQTT or Zigbee — server resolves transport). */
  commandStacklight: async (body: {
    signalName: string;
    stacklight?: string;
    hostname?: string;
    leaf?: string;
    payload?: string;
    baseTopic?: string;
  }): Promise<{ ok: boolean; transport?: string; topic?: string }> => {
    const response = await axios.post<{ ok: boolean; transport?: string; topic?: string }>(
      `${API_BASE}/stacklight/command`,
      body,
    );
    return response.data;
  },
};

export const shopfloorApi = {
  get: async (): Promise<{ signals: PlacedSignal[] }> => {
    const response = await axios.get<{ signals: PlacedSignal[] }>(`${API_BASE}/shopfloor`);
    const raw = response.data.signals || [];
    return { signals: raw };
  },

  save: async (body: { signals: PlacedSignal[] }): Promise<{ signals: PlacedSignal[] }> => {
    const response = await axios.put<{ ok: boolean; signals: PlacedSignal[] }>(`${API_BASE}/shopfloor`, body);
    return { signals: response.data.signals || [] };
  },
};

export const alertsApi = {
  get: async (): Promise<{
    rules: TemperatureAlertRule[];
    flowRules: TemperatureAlertRule[];
    events: AlertEvent[];
    channels: AlertChannelsPublic;
    pushoverConfigured: boolean;
    mailConfigured: boolean;
  }> => {
    const response = await axios.get<{
      rules?: TemperatureAlertRule[];
      flowRules?: TemperatureAlertRule[];
      events?: AlertEvent[];
      channels?: AlertChannelsPublic;
      pushoverConfigured?: boolean;
      mailConfigured?: boolean;
    }>(`${API_BASE}/alerts`);
    return {
      rules: response.data.rules || [],
      flowRules: response.data.flowRules || [],
      events: response.data.events || [],
      channels: response.data.channels || emptyChannelsPublic(),
      pushoverConfigured: response.data.pushoverConfigured === true,
      mailConfigured: response.data.mailConfigured === true,
    };
  },

  save: async (body: {
    rules?: TemperatureAlertRule[];
    channels?: AlertChannelsDraft;
  }): Promise<{
    rules: TemperatureAlertRule[];
    events: AlertEvent[];
    channels: AlertChannelsPublic;
    pushoverConfigured: boolean;
    mailConfigured: boolean;
  }> => {
    const response = await axios.put<{
      ok: boolean;
      rules?: TemperatureAlertRule[];
      events?: AlertEvent[];
      channels?: AlertChannelsPublic;
      pushoverConfigured?: boolean;
      mailConfigured?: boolean;
    }>(`${API_BASE}/alerts`, body);
    return {
      rules: response.data.rules || [],
      events: response.data.events || [],
      channels: response.data.channels || emptyChannelsPublic(),
      pushoverConfigured: response.data.pushoverConfigured === true,
      mailConfigured: response.data.mailConfigured === true,
    };
  },

  test: async (channel: 'pushover' | 'mail' | 'all', channels?: AlertChannelsDraft): Promise<void> => {
    await axios.post(`${API_BASE}/alerts/test`, { channel, channels });
  },

  ack: async (ruleId: string): Promise<{ events: AlertEvent[] }> => {
    const response = await axios.post<{ ok: boolean; events?: AlertEvent[] }>(
      `${API_BASE}/alerts/ack`,
      { ruleId },
    );
    return { events: response.data.events || [] };
  },
};

export interface HubInstance {
  id: string;
  name: string;
  customer: string;
  url: string;
  version: string;
  devices: number;
  devicesOnline: number;
  active: boolean;
  seenAgoSec: number | null;
  managed: boolean;
}

export interface HubInstanceSecrets {
  adminUser: string;
  adminPassword: string;
}

export const instanceApi = {
  list: async (): Promise<HubInstance[]> => {
    const response = await axios.get<{ instances: HubInstance[] }>(`${API_BASE}/instances`);
    return response.data.instances || [];
  },

  create: async (body: { name?: string; customer?: string }): Promise<{
    instance: HubInstance;
    secrets: HubInstanceSecrets;
    log?: string;
  }> => {
    const response = await axios.post(`${API_BASE}/instances`, body);
    return response.data;
  },

  update: async (id: string): Promise<{ log?: string }> => {
    const response = await axios.post(`${API_BASE}/instances/${encodeURIComponent(id)}/update`);
    return response.data;
  },
};
